// Service Steady PWA — src/components/RangePicker.tsx — 2026-06-30
//
// Date-range picker for Reports: a compact trigger button + a bottom sheet
// (mobile) / centered modal (desktop) with calendar presets and a custom
// Start/End range. Mirrors the FilterSheet sheet pattern (fixed overlay,
// rounded-t-2xl, grab handle, Esc / overlay-tap dismiss). Custom dates use
// native <input type="date"> — on iOS that opens the system wheel picker,
// which is more touch-friendly than any JS calendar and costs zero bundle.

import { useEffect, useState } from 'react';
import { Calendar, Check, ChevronDown, X } from 'lucide-react';
import type { ReportsPeriod } from '@/lib/api';

export interface RangeSelection {
  period: ReportsPeriod;
  /** Set only when period === 'custom' (YYYY-MM-DD, inclusive). */
  from?: string;
  to?: string;
}

interface RangePickerProps {
  value: RangeSelection;
  onChange: (next: RangeSelection) => void;
  /** Server-resolved "Jun 2 – Jul 1, 2026" label (shown under the trigger). */
  resolvedLabel?: string;
}

// Preset order matches the runbook. Legacy 'week' (rolling 7d) is accepted
// from old URLs but not offered — the calendar presets replace it.
const PRESETS: { value: ReportsPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'month', label: 'Last 30 days' },
];

export function presetLabel(sel: RangeSelection): string {
  if (sel.period === 'custom') return 'Custom range';
  if (sel.period === 'week') return 'Last 7 days';
  return PRESETS.find((p) => p.value === sel.period)?.label ?? 'Last 30 days';
}

export function RangePicker({ value, onChange, resolvedLabel }: RangePickerProps) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(value.period === 'custom');
  const [draftFrom, setDraftFrom] = useState(value.from ?? '');
  const [draftTo, setDraftTo] = useState(value.to ?? '');

  // Re-seed drafts each time the sheet opens so Cancel never leaks stale edits.
  useEffect(() => {
    if (open) {
      setCustomMode(value.period === 'custom');
      setDraftFrom(value.from ?? '');
      setDraftTo(value.to ?? '');
    }
  }, [open, value.period, value.from, value.to]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const applyPreset = (p: ReportsPeriod): void => {
    onChange({ period: p });
    setOpen(false);
  };

  const customValid = Boolean(draftFrom && draftTo && draftFrom <= draftTo);
  const applyCustom = (): void => {
    if (!customValid) return;
    onChange({ period: 'custom', from: draftFrom, to: draftTo });
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="ss-press inline-flex items-center gap-1.5 rounded-ss-sm bg-ink-100 px-3 py-1.5 text-[13px] font-semibold text-ink-900 dark:bg-[#1E293B] dark:text-[#E2E8F0]"
      >
        <Calendar className="h-3.5 w-3.5 text-ink-500 dark:text-[#94A3B8]" aria-hidden="true" />
        {presetLabel(value)}
        <ChevronDown className="h-3.5 w-3.5 text-ink-500 dark:text-[#94A3B8]" aria-hidden="true" />
      </button>
      {resolvedLabel ? (
        <div className="mt-1 text-xs text-ink-500 dark:text-[#94A3B8]">{resolvedLabel}</div>
      ) : null}

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Report date range"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex w-full max-h-[85vh] max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-[#0F172A] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle (mobile) */}
            <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden="true">
              <div className="h-1 w-9 rounded-full bg-ink-200 dark:bg-[#334155]" />
            </div>
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
              <div className="text-base font-semibold text-ink-900 dark:text-white">
                Date range
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-50 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              className="flex-1 overflow-y-auto"
              style={{ paddingBottom: 'calc(var(--tab-h, 64px) + env(safe-area-inset-bottom, 0px))' }}
            >
              {/* Presets */}
              <ul role="listbox" aria-label="Date range presets" className="divide-y divide-ink-100 dark:divide-[#1F2A37]">
                {PRESETS.map((p) => {
                  const active = !customMode && p.value === value.period;
                  return (
                    <li key={p.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => applyPreset(p.value)}
                        className={[
                          'flex w-full items-center gap-3 px-4 py-2.5 text-left text-[15px] transition-colors',
                          active
                            ? 'bg-[#E6F1FB] font-semibold text-[#0C447C] dark:bg-[#15315A] dark:text-[#9CC4F0]'
                            : 'text-ink-800 hover:bg-ink-50 dark:text-[#E2E8F0] dark:hover:bg-[#1E293B]',
                        ].join(' ')}
                      >
                        <Check
                          className={['h-4 w-4 flex-shrink-0', active ? 'opacity-100' : 'opacity-0'].join(' ')}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{p.label}</span>
                      </button>
                    </li>
                  );
                })}
                {/* Custom toggle row */}
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={customMode}
                    onClick={() => setCustomMode(true)}
                    className={[
                      'flex w-full items-center gap-3 px-4 py-2.5 text-left text-[15px] transition-colors',
                      customMode
                        ? 'bg-[#E6F1FB] font-semibold text-[#0C447C] dark:bg-[#15315A] dark:text-[#9CC4F0]'
                        : 'text-ink-800 hover:bg-ink-50 dark:text-[#E2E8F0] dark:hover:bg-[#1E293B]',
                    ].join(' ')}
                  >
                    <Check
                      className={['h-4 w-4 flex-shrink-0', customMode ? 'opacity-100' : 'opacity-0'].join(' ')}
                      aria-hidden="true"
                    />
                    <span className="flex-1">Custom range…</span>
                  </button>
                </li>
              </ul>

              {/* Custom Start/End — native date inputs (iOS wheel picker) */}
              {customMode ? (
                <div className="border-t border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
                  <div className="grid grid-cols-2 gap-2.5">
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                        Start
                      </span>
                      <input
                        type="date"
                        value={draftFrom}
                        max={draftTo || undefined}
                        onChange={(e) => setDraftFrom(e.target.value)}
                        className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                        End
                      </span>
                      <input
                        type="date"
                        value={draftTo}
                        min={draftFrom || undefined}
                        onChange={(e) => setDraftTo(e.target.value)}
                        className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="ss-press flex-1 rounded-xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-center text-sm font-semibold text-ink-900 dark:border-[#334155] dark:bg-[#0F172A] dark:text-[#E2E8F0]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={applyCustom}
                      disabled={!customValid}
                      className="ss-press flex-1 rounded-xl px-3 py-2.5 text-center text-sm font-semibold text-white disabled:opacity-40"
                      style={{ background: 'var(--ss-accent, #2563EB)' }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
